/**
 * Company-wide switch over what a carrier who quoted and lost sees on this
 * shipper company's booked loads: the exact booked price, or only the
 * competitiveness band. Shipper company administrators only — carrier companies
 * have no such setting. See
 * docs/engineering/design/shipper_company_price_reveal_control_design.md.
 */
import { useState } from "react";

import { FormControlLabel, Stack, Switch, Tooltip } from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { useWriterComponentState } from "hooks/useWriterComponentState";

import { ClientAppEvent } from "../../../../constants";

import type { ChangeEvent } from "react";

interface BookedPriceRevealToggleProps {
  companyId: number;
  enabled: boolean;
  onSuccess: () => void;
}

const LABEL = "Reveal booked price to carriers who quoted and lost";

const TOOLTIP =
  "Losing carriers see the final price to help them price your next similar load " +
  "closer to what you want to pay.";

export function BookedPriceRevealToggle(props: BookedPriceRevealToggleProps) {
  const { companyId, enabled, onSuccess } = props;
  const posthog = usePostHog();

  // Mirrors the switch so it moves under the user's finger rather than after
  // the round trip. `onWriteFailure` puts it back to the fetched value, so a
  // rejected write never leaves the switch showing something the server did
  // not store.
  const [checked, setChecked] = useState(enabled);

  // Adjust state when the stored value changes, per React's documented pattern
  // for this case. useState seeds once and the page never remounts this
  // component, so otherwise the switch would keep showing the optimistic value
  // after a refetch brought back a different one (another admin, or ops).
  const [storedEnabled, setStoredEnabled] = useState(enabled);
  if (storedEnabled !== enabled) {
    setStoredEnabled(enabled);
    setChecked(enabled);
  }

  const { loading, error, write } = useWriterComponentState({
    writeConfig: {
      method: "patch",
      url: `/companies/${companyId}/booked-price-reveal`,
    },
    errorMessage: "Failed to update booked price sharing",
    errorMessageKey: "message",
    onWriteFailure: () => setChecked(enabled),
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.checked;
    // Captured before the write resolves, so an interaction whose write fails
    // is still recorded.
    posthog?.capture(ClientAppEvent.SHIPPER_BOOKED_PRICE_REVEAL_TOGGLED, {
      enabled: next,
      company_id: companyId,
    });
    setChecked(next);
    write(onSuccess, { enabled: next });
  };

  return (
    // alignItems keeps FormControlLabel at its content width. A column Stack
    // stretches its children, which would widen the label — and with it the
    // tooltip's hover target — across the whole page.
    <Stack direction="column" alignItems="flex-start">
      <Tooltip title={TOOLTIP}>
        <FormControlLabel
          sx={{ mr: 0 }}
          control={
            <Switch
              checked={checked}
              onChange={handleChange}
              disabled={loading}
            />
          }
          label={LABEL}
        />
      </Tooltip>
      <ErrorTypography error={error} />
    </Stack>
  );
}
